import MyChartUser, { AUTHENTICATION_STATUS } from "./mychart-user.es6.js";
import LuminisServiceUtils from "@js/utils/luminis-service-utils.es6.js";
import { detectIncognito } from "detectincognitojs";

export default class LoginPage {
  constructor($) {
    // only want this back button to behave like this if its truly on the login page.
    this.backButton = document.querySelector(
      '.login-to-mychart-node .back-link',
    );
    this.loginButton = document.querySelector(
      '.field--name-field-primary-cta a',
    );
    this.loginForm = $('#login-to-mychart-form');
    this.previousPage = document.referrer === "" ? window.location.href : document.referrer;
    this.maskButton = document.querySelector('.toggleMask');
    this.passwordInput = document.querySelector('#password');
    this.usernameInput = document.querySelector('#username');
  }

  static isOnLoginPage() {
    const element = document.querySelector('.login-to-mychart-node');
    return !!element;
  }

  // Checks URL for MyChart redirect errors and displays them.
  static displayMyChartRedirectError() {
    const urlParams = new URLSearchParams(window.location.search);
    const errorCode = urlParams.get('error');


    // Only proceed if the specific error parameters from MyChart OAuth are present.
    if (errorCode) {
      const errorContainer = document.querySelector('.error-message-container');
      if (!errorContainer) {
        return;
      }

      // Use the base URL from Drupal settings for our error handler config.
      // This is used for generating links in messages.
      const myChartBaseUrl = `${drupalSettings?.luminis?.oauthMychartUrl}/`;

      // note: we only support english atm but the error handler contains es for future
      // proofing
      const message = {
        en: `<p>Incorrect username or password. Try again or <a href="${myChartBaseUrl}passwordreset.asp">reset your password.</a></p>`,
        es: `<p>Nombre de usuario o contraseña incorrectos. Inténtalo de nuevo o <a href="${myChartBaseUrl}passwordreset.asp">restablece tu contraseña.</a></p>`
      };

      // Use innerHTML to render the message, which may contain HTML tags.
      errorContainer.innerHTML = message['en'];
      errorContainer.classList.remove('hidden');
      document.querySelector('.mychart-iframe').classList.add('error');
    }
  }

  async setReturnLocationsFromLoginPageAndMychart() {
    if (this.backButton) {
      this.backButton.addEventListener('click', () => {
        window.location = this.previousPage;
      });
      this.backButton.addEventListener('keypress', () => {
        window.location = this.previousPage;
      });
    }

    const path = window.location.pathname.replace(/\/$/, '');
    // Strictly matches '/login' with an optional two-letter language prefix.
    const onLoginNode = /^\/([a-z]{2}\/)?login$/.test(path);

    // do not redirect because we are already on the logout page.
    if (onLoginNode) {
      // log the user out of the REST API in case they came from mychart specifically
      const luminisServiceUtils = new LuminisServiceUtils();
      await luminisServiceUtils.logout(true);

      // only call this on the login page (and not the login modal or else it may log out a logged in user)
      LoginPage.initializeIframe();
    }
  }

  static initializeIframe(nextUrl) {
    if (!nextUrl) {
      nextUrl = MyChartUser.getLoginResumeUrl();
    }

    MyChartUser.storeLoginResumeUrl(nextUrl);

    const iframe = document.querySelector(".mychart-iframe");
    const baseWidgetPath = drupalSettings?.luminis?.oauthMychartUrl + '/Authentication/Login/LoginWidget';
    const encodedNextUrl = encodeURIComponent(nextUrl);
    const relay = `resumeUrl=${encodedNextUrl}`;
    const relayEncoded = encodeURIComponent(relay);
    const postLoginUrl = `Extensibility/Redirection/FdiRedirection?option=oauthehrlogin&relay=${relayEncoded}`;
    const postLoginUrlEncoded = encodeURIComponent(postLoginUrl);
    const fullUrl = `${baseWidgetPath}?postloginurl=${postLoginUrlEncoded}`;
    iframe.src = fullUrl;
  }

  sendForm($) {
    const loginFormArray = this.loginForm.serializeArray();

    let isInvalid = false;

    const $usernameGroup = $('.username-group');
    const $passwordGroup = $('.password-group');
    const $authenticationError = $('.authentication-error');

    $usernameGroup.removeClass('error');
    $passwordGroup.removeClass('error');
    $authenticationError.removeClass('error');

    loginFormArray.forEach(item => {
      if (item.name === 'username' && item.value === '') {
        $usernameGroup.addClass('error');
        isInvalid = true;
      }
      if (item.name === 'password' && item.value === '') {
        $passwordGroup.addClass('error');
        isInvalid = true;
      }
    });

    if (isInvalid) {
      return;
    }

    const username = loginFormArray[0].value;
    const password = loginFormArray[1].value;
    MyChartUser.login($, username, password, status => {
      LoginPage.handleAuthenticationError($, status, username);
    });
  }

  static handleAuthenticationError($, status, username) {
    switch (status) {
      case AUTHENTICATION_STATUS.PASSWORD_EXPIRED: {
        const myChartPasswordResetLink = document.querySelector(
          '.field--name-field-reset-mychart-password a',
        ).href;
        window.location = `${myChartPasswordResetLink}&username=${username}`;
        break;
      }
      default: {
        const $authenticationError = $('.authentication-error');
        $authenticationError.addClass('error');
        break;
      }
    }
  }

  setClickHandlers($) {
    if (this.backButton) {
      this.backButton.addEventListener('click', () => {
        window.location = this.previousPage;
      });
      this.backButton.addEventListener('keypress', () => {
        window.location = this.previousPage;
      });
    }
    this.loginButton.addEventListener('click', event => {
      event.preventDefault();
      this.sendForm($);
    });
    this.loginButton.addEventListener('keypress', event => {
      event.preventDefault();
      this.sendForm($);
    });
    // mousedown instead of click so that edge case blur events don't interfere.
    this.maskButton.addEventListener('mousedown', () => {
      this.togglePasswordMask();
    });
    this.maskButton.addEventListener('keypress', () => {
      this.togglePasswordMask();
    });
    this.usernameInput.addEventListener('keypress', event => {
      // Enter key corresponds to number 13
      if (event.which === 13) {
        this.sendForm($);
      }
    });
    this.passwordInput.addEventListener('keypress', event => {
      // Enter key corresponds to number 13
      if (event.which === 13) {
        this.sendForm($);
      }
    });
  }

  /**
   * Enables any button that has been disabled.
   *
   * Some buttons may be disabled until the form is ready and event listeners are attached.
   */
  enableButtons() {
    this.loginButton.classList.remove('disabled');
    this.loginButton.setAttribute('tabindex', '0');
  }

  togglePasswordMask() {
    const toggleValue = this.maskButton.checked;

    if (toggleValue) {
      this.passwordInput.type = 'text';
    } else {
      this.passwordInput.type = 'password';
    }
    this.maskButton.classList.toggle('closed-eye');
    this.maskButton.classList.toggle('open-eye');
  }

  static async detectThirdPartyCookiesBlocked() {
    // Detect Incognito browsers that epic doesn't like
    const result = await detectIncognito();
    if (result.browserName === 'Chrome' && result.isPrivate || result.browserName === 'Edge' && result.isPrivate) {
      return true;
    }

    return result.browserName === "Firefox" || result.browserName === "Safari";
  }
}

(async $ => {
  const loginBoxIsOnPage = LoginPage.isOnLoginPage();

  // if the login box is present, run this code
  if (loginBoxIsOnPage) {
    // we need to set the src url dynamically to go back to where we are coming from
    const loginPage = new LoginPage($);

    // Call the method to check for and display URL errors upon page load.
    LoginPage.displayMyChartRedirectError();

    LoginPage.detectThirdPartyCookiesBlocked().then(blocked => {
      if (blocked) {
        document.querySelector('.mychart-warning')?.classList.remove('hidden');
      }
    });

    await loginPage.setReturnLocationsFromLoginPageAndMychart();
  }
})(jQuery);
